import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, RefreshCw, Plane, Navigation, Gauge, Radio, Info, LocateFixed, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config';

/* ─── Helpers ──────────────────────────────────────────────────────────────── */
const getAltFt = (alt) => {
  if (alt === 'ground' || alt === null || alt === undefined) return 0;
  return typeof alt === 'number' ? alt : parseInt(alt) || 0;
};

const getAltLabel = (alt) => {
  if (alt === 'ground') return 'On Ground';
  const ft = getAltFt(alt);
  if (ft === 0) return 'On Ground';
  return `${ft.toLocaleString()} ft`;
};

// Color by altitude tier
const getPlaneColor = (alt_baro, isSelected) => {
  if (isSelected) return '#FF4500';
  const ft = getAltFt(alt_baro);
  if (ft === 0) return '#94a3b8';        // ground – gray
  if (ft < 10000) return '#fbbf24';      // low – amber
  if (ft < 28000) return '#34d399';      // mid – emerald
  return '#60a5fa';                       // cruise – blue
};

const getVertLabel = (vert) => {
  if (!vert || vert === 0) return null;
  return vert > 64 ? '↑ Climbing' : vert < -64 ? '↓ Descending' : '→ Level';
};

// Distance in nm between two lat/lon points
const distNm = (la1, lo1, la2, lo2) => {
  const R = 3440.065;
  const dLat = (la2 - la1) * Math.PI / 180;
  const dLon = (lo2 - lo1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/* ─── Inject tracker CSS once ──────────────────────────────────────────────── */
let trackerCSSInjected = false;
function injectTrackerCSS() {
  if (trackerCSSInjected) return;
  trackerCSSInjected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes trackerPulse {
      0%   { transform: scale(1);   opacity: 0.7; }
      60%  { transform: scale(2.2); opacity: 0; }
      100% { transform: scale(2.2); opacity: 0; }
    }
    .tracker-pulse-ring {
      position: absolute; inset: -7px;
      border: 2px solid #FF4500; border-radius: 50%;
      animation: trackerPulse 1.8s ease-out infinite;
      pointer-events: none;
    }
    .tracker-plane { cursor: pointer; }
    .tracker-plane:hover { filter: brightness(1.35); }
  `;
  document.head.appendChild(s);
}

/* ─── Plane icon factory ────────────────────────────────────────────────────── */
function createPlaneIcon(L, track = 0, color = '#60a5fa', selected = false) {
  const sz = selected ? 26 : 20;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="${sz}" height="${sz}">
      <ellipse cx="16" cy="16" rx="2.5" ry="12" fill="${color}"/>
      <path d="M16,11 L1,22 L16,19 L31,22 Z" fill="${color}"/>
      <path d="M16,24 L9,30 L16,28 L23,30 Z" fill="${color}"/>
    </svg>`;
  const ring = selected
    ? `<div class="tracker-pulse-ring"></div>`
    : '';
  return L.divIcon({
    html: `<div class="tracker-plane" style="position:relative;width:${sz}px;height:${sz}px;transform:rotate(${track}deg);transform-origin:center;filter:drop-shadow(0 0 4px ${color}88)">${svg}${ring}</div>`,
    className: '',
    iconSize: [sz, sz],
    iconAnchor: [sz / 2, sz / 2],
  });
}

/* ─── Component ─────────────────────────────────────────────────────────────── */
export default function FlightTracker() {
  const navigate       = useNavigate();
  const { user }       = useAuth();
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const LRef           = useRef(null);
  const markersRef     = useRef(new Map());   // hex → marker
  const selectedACRef  = useRef(null);
  const lastFetchCenterRef = useRef({ lat: 25, lon: 51 });
  const [loggingFlight, setLoggingFlight] = useState(false);

  const [aircraft, setAircraft]       = useState([]);
  const [selectedAC, setSelectedAC]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [fetching, setFetching]       = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching]     = useState(false);
  const [countdown, setCountdown]     = useState(15);
  const [lastUpdate, setLastUpdate]   = useState(null);
  const [mapReady, setMapReady]       = useState(false);

  // Sync selectedAC ref
  useEffect(() => { selectedACRef.current = selectedAC; }, [selectedAC]);

  /* ── Update marker icons when selection changes ── */
  useEffect(() => {
    const L = LRef.current;
    if (!L) return;
    for (const [hex, marker] of markersRef.current) {
      const ac = marker._acData;
      if (!ac) continue;
      const isSelected = selectedAC?.hex === hex;
      marker.setIcon(createPlaneIcon(L, ac.track || 0, getPlaneColor(ac.alt_baro, isSelected), isSelected));
      if (isSelected) marker.setZIndexOffset(1000);
      else marker.setZIndexOffset(50);
    }
  }, [selectedAC]);

  /* ── Draw / update markers ── */
  const updateMarkers = useCallback((newAC) => {
    const L = LRef.current;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    const seen = new Set();
    newAC.forEach(ac => {
      if (!ac.lat || !ac.lon) return;
      seen.add(ac.hex);
      const isSelected = selectedACRef.current?.hex === ac.hex;
      const color = getPlaneColor(ac.alt_baro, isSelected);
      const icon  = createPlaneIcon(L, ac.track || 0, color, isSelected);

      if (markersRef.current.has(ac.hex)) {
        const m = markersRef.current.get(ac.hex);
        m.setLatLng([ac.lat, ac.lon]);
        m.setIcon(icon);
        m._acData = ac;
      } else {
        const m = L.marker([ac.lat, ac.lon], { icon, zIndexOffset: isSelected ? 1000 : 50 });
        m._acData = ac;
        m.on('click', () => setSelectedAC(prev => prev?.hex === ac.hex ? null : ac));
        m.addTo(map);
        markersRef.current.set(ac.hex, m);
      }
    });

    // Remove stale markers
    for (const [hex, m] of markersRef.current) {
      if (!seen.has(hex)) {
        map.removeLayer(m);
        markersRef.current.delete(hex);
      }
    }
  }, []);

  /* ── Fetch aircraft for current map view ── */
  const fetchAircraft = useCallback(async (centerOverride) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    setFetching(true);
    const center = centerOverride || map.getCenter();
    lastFetchCenterRef.current = { lat: center.lat, lon: center.lng };
    try {
      const res = await axios.get(`${API}/track/region`, {
        params: { lat: center.lat.toFixed(4), lon: center.lng.toFixed(4), radius: 250 },
      });
      const ac = res.data.aircraft || [];
      setAircraft(ac);
      updateMarkers(ac);
      setLastUpdate(new Date());
      setCountdown(15);
    } catch (err) {
      if (err.response?.status === 504) toast.error('ADS-B service timed out');
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, [updateMarkers]);

  /* ── Init map ── */
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    let mounted = true;

    (async () => {
      injectTrackerCSS();
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      LRef.current = L;

      if (!mounted || !mapRef.current || mapInstanceRef.current) return;

      const map = L.map(mapRef.current, {
        center: [25, 51],
        zoom: 5,
        minZoom: 2,
        maxZoom: 12,
        zoomControl: true,
        scrollWheelZoom: true,
        attributionControl: false,
      });

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
        { subdomains: 'abcd', maxZoom: 14 }
      ).addTo(map);

      L.control.attribution({ prefix: false }).addTo(map);
      map.attributionControl.addAttribution('© <a href="https://carto.com">CARTO</a> | ADS-B: <a href="https://adsb.lol">adsb.lol</a>');

      // Re-fetch when user pans significantly (tighter threshold for better coverage)
      map.on('moveend', () => {
        const c = map.getCenter();
        const last = lastFetchCenterRef.current;
        if (distNm(c.lat, c.lng, last.lat, last.lon) > 80) {
          fetchAircraft({ lat: c.lat, lng: c.lng });
        }
      });

      mapInstanceRef.current = map;
      setMapReady(true);

      // ── Try user geolocation ──────────────────────────────────────
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            if (mapInstanceRef.current) {
              mapInstanceRef.current.setView([latitude, longitude], 6, { animate: true });
              lastFetchCenterRef.current = { lat: latitude, lon: longitude };
            }
          },
          () => { /* user denied or unavailable — stay on Gulf */ },
          { timeout: 6000, maximumAge: 60000 }
        );
      }
    })();

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [fetchAircraft]);

  /* ── Initial fetch + auto-refresh ── */
  useEffect(() => {
    if (!mapReady) return;
    fetchAircraft();
    const interval = setInterval(() => fetchAircraft(), 15000);
    return () => clearInterval(interval);
  }, [mapReady, fetchAircraft]);

  /* ── Countdown ticker ── */
  useEffect(() => {
    const t = setInterval(() => setCountdown(p => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  /* ── Search ── */
  const handleSearch = async () => {
    const q = searchInput.trim();
    if (!q) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/track/flight/${encodeURIComponent(q)}`);
      const ac  = res.data.aircraft;
      setSelectedAC(ac);
      const map = mapInstanceRef.current;
      if (map && ac.lat && ac.lon) {
        map.setView([ac.lat, ac.lon], 8, { animate: true });
        await fetchAircraft({ lat: ac.lat, lng: ac.lon });
      }
      toast.success(`Tracking ${res.data.callsign}`);
    } catch (err) {
      const msg = err.response?.data?.detail || `Flight ${q.toUpperCase()} not found`;
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  /* ── Log a tracked flight to the Fly N Out route system ── */
  const handleLogFlight = async () => {
    const callsign = selectedAC?.flight?.trim();
    if (!callsign) return;
    if (!user) { toast.error('Log in to save this flight to your profile'); return; }
    setLoggingFlight(true);
    try {
      const res = await axios.post(`${API}/flights/checkin`, { flight_number: callsign }, { withCredentials: true });
      toast.success(`${callsign} logged! Opening route community…`);
      setTimeout(() => navigate(`/route/${res.data.route_id}`), 800);
    } catch {
      toast.error('Could not log flight — try again');
    } finally {
      setLoggingFlight(false);
    }
  };

  /* ── Locate me ── */
  const handleLocateMe = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const map = mapInstanceRef.current;
        if (map) {
          map.setView([latitude, longitude], 7, { animate: true });
          fetchAircraft({ lat: latitude, lng: longitude });
        }
      },
      () => toast.error('Could not get your location'),
      { timeout: 8000 }
    );
  };

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="flex flex-col min-h-screen bg-[#0a0f1a]">
      <Navbar />

      {/* Full-height tracker area */}
      <div className="relative flex-1" style={{ height: 'calc(100vh - 64px)' }}>

        {/* ── Leaflet map ── */}
        <div ref={mapRef} className="absolute inset-0 z-0" />

        {/* ── Loading skeleton ── */}
        {loading && (
          <div className="absolute inset-0 z-[8000] flex items-center justify-center bg-[#0a0f1a]">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 rounded-full border-2 border-[#FF4500]/30 border-t-[#FF4500] animate-spin mx-auto" />
              <p className="text-slate-400 text-sm">Connecting to ADS-B network…</p>
            </div>
          </div>
        )}

        {/* ── Top search bar ── */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[9000] w-full max-w-[220px] sm:max-w-sm px-3 sm:px-4">
          <div className="flex items-center gap-2 bg-[#0f172a]/92 backdrop-blur-md border border-white/10 rounded-2xl px-3 py-2 shadow-2xl">
            <Search className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <input
              type="text"
              placeholder="EK101, SV001…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1 bg-transparent text-white text-sm placeholder:text-slate-600 focus:outline-none min-w-0"
              data-testid="tracker-search-input"
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSelectedAC(null); }} className="text-slate-600 hover:text-slate-300">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <Button
              size="sm"
              onClick={handleSearch}
              disabled={searching || !searchInput.trim()}
              className="fly-btn-primary h-7 px-3 text-xs rounded-xl flex-shrink-0"
              data-testid="tracker-search-btn"
            >
              {searching ? '…' : 'Track'}
            </Button>
          </div>
        </div>

        {/* ── Locate Me button — bottom-right on mobile ── */}
        <button
          onClick={handleLocateMe}
          className="absolute bottom-20 sm:bottom-6 right-4 z-[9000] bg-[#0f172a]/85 backdrop-blur-sm border border-white/10 rounded-xl p-2.5 text-slate-400 hover:text-white hover:bg-[#0f172a] transition-colors shadow-lg sm:hidden"
          title="Locate me"
          data-testid="tracker-locate-btn"
        >
          <LocateFixed className="w-4 h-4" />
        </button>

        {/* ── Stats pill — top-right on mobile, top-left on desktop ── */}
        <div className="absolute top-4 right-4 sm:left-4 sm:right-auto z-[9000] flex items-center gap-2 bg-[#0f172a]/85 backdrop-blur-sm border border-white/10 rounded-xl px-2.5 py-1.5 text-xs text-slate-400 shadow-lg">
          <span className="w-1.5 h-1.5 rounded-full bg-[#34d399] animate-pulse" />
          <span className="text-white font-semibold">{aircraft.length}</span>
          <span className="hidden sm:inline">aircraft</span>
          <span className="text-slate-600">·</span>
          <RefreshCw className={`w-3 h-3 ${fetching ? 'animate-spin text-[#FF4500]' : ''}`} />
          <span className="hidden sm:inline">{fetching ? 'updating' : `${countdown}s`}</span>
        </div>

        {/* ── Legend — hidden on mobile to save space ── */}
        <div className="hidden sm:block absolute bottom-6 left-4 z-[9000] bg-[#0f172a]/88 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-2.5 text-[11px] text-slate-400 space-y-1.5 shadow-lg">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1">Altitude</p>
          {[
            ['#60a5fa', '> 28,000 ft  Cruise'],
            ['#34d399', '10 – 28k ft  Climb/Descent'],
            ['#fbbf24', '< 10,000 ft  Low altitude'],
            ['#94a3b8', 'On ground'],
          ].map(([c, label]) => (
            <div key={c} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: c }} />
              <span>{label}</span>
            </div>
          ))}
          <div className="border-t border-white/10 pt-1.5 mt-1 text-[10px] text-slate-600">
            Click any aircraft for details
          </div>
        </div>

        {/* ── Browse link ── */}
        <div className="hidden sm:block absolute bottom-6 right-4 z-[9000]">
          <Link
            to="/routes"
            className="flex items-center gap-1.5 bg-[#0f172a]/85 backdrop-blur-sm border border-[#FF4500]/20 rounded-xl px-3 py-1.5 text-xs text-[#FF4500] hover:bg-[#FF4500]/10 transition-colors shadow-lg"
          >
            <Plane className="w-3 h-3" /> Browse Route Communities
          </Link>
        </div>

        {/* ── Aircraft detail panel — right side on desktop, bottom sheet on mobile ── */}
        <AnimatePresence>
          {selectedAC && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
              className="absolute sm:right-4 sm:top-[72px] inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-auto z-[9000] sm:w-72 bg-[#0f172a]/96 backdrop-blur-xl border-t sm:border border-white/10 sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
              data-testid="tracker-detail-panel"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#FF4500]/20 to-transparent px-4 pt-4 pb-3 flex items-start justify-between">
                <div>
                  <p className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
                    {selectedAC.flight?.trim() || selectedAC.hex?.toUpperCase()}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-400">{selectedAC.t || '—'}</span>
                    {selectedAC.r && <span className="text-[10px] text-slate-500 bg-white/5 px-1.5 py-0.5 rounded">{selectedAC.r}</span>}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAC(null)}
                  className="text-slate-500 hover:text-white transition-colors p-1"
                  data-testid="tracker-panel-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Stats grid */}
              <div className="px-4 py-3 grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    <Navigation className="w-3 h-3 text-slate-500" />
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Altitude</p>
                  </div>
                  <p className="text-sm font-semibold text-white">{getAltLabel(selectedAC.alt_baro)}</p>
                  {selectedAC.vert_rate != null && (
                    <p className="text-[10px] text-slate-500 mt-0.5">{getVertLabel(selectedAC.vert_rate)}</p>
                  )}
                </div>

                <div className="bg-white/5 rounded-xl p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    <Gauge className="w-3 h-3 text-slate-500" />
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Speed</p>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {selectedAC.gs ? `${Math.round(selectedAC.gs)} kts` : '—'}
                  </p>
                  {selectedAC.ias && (
                    <p className="text-[10px] text-slate-500 mt-0.5">IAS {Math.round(selectedAC.ias)} kts</p>
                  )}
                </div>

                <div className="bg-white/5 rounded-xl p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    <Plane className="w-3 h-3 text-slate-500" />
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Heading</p>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {selectedAC.track != null ? `${Math.round(selectedAC.track)}°` : '—'}
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    <Radio className="w-3 h-3 text-slate-500" />
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Squawk</p>
                  </div>
                  <p className="text-sm font-semibold text-white">{selectedAC.squawk || '—'}</p>
                </div>
              </div>

              {/* ICAO / position */}
              <div className="px-4 pb-3 flex items-center gap-3 text-[11px] text-slate-600">
                <span>ICAO: {selectedAC.hex?.toUpperCase()}</span>
                <span>·</span>
                <span>{selectedAC.lat?.toFixed(3)}°, {selectedAC.lon?.toFixed(3)}°</span>
              </div>

              {/* Altitude bar */}
              {getAltFt(selectedAC.alt_baro) > 0 && (
                <div className="px-4 pb-3">
                  <div className="w-full bg-white/5 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (getAltFt(selectedAC.alt_baro) / 42000) * 100)}%`,
                        background: getPlaneColor(selectedAC.alt_baro, false),
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-slate-600 mt-0.5">
                    <span>0 ft</span><span>42,000 ft</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="px-4 pb-4 space-y-2">
                {/* Log This Flight — connects live tracker to route community */}
                {selectedAC.flight?.trim() && (
                  <button
                    onClick={handleLogFlight}
                    disabled={loggingFlight}
                    className="flex items-center justify-between w-full bg-[#FF4500] hover:bg-[#e03d00] text-white rounded-xl px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60"
                    data-testid="tracker-log-flight-btn"
                  >
                    <span>{loggingFlight ? 'Logging…' : 'Log This Flight'}</span>
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
                {selectedAC.flight?.trim() && (
                  <Link
                    to={`/route/${selectedAC.flight.trim().replace(/\s+/g, '')}`}
                    className="flex items-center justify-between w-full bg-[#FF4500]/15 hover:bg-[#FF4500]/25 text-[#FF4500] rounded-xl px-3 py-2 text-xs font-semibold transition-colors"
                    data-testid="tracker-view-community-btn"
                  >
                    <span>View Route Community</span>
                    <Plane className="w-3.5 h-3.5" />
                  </Link>
                )}
                <button
                  onClick={() => {
                    const map = mapInstanceRef.current;
                    if (map && selectedAC.lat && selectedAC.lon) {
                      map.setView([selectedAC.lat, selectedAC.lon], 9, { animate: true });
                    }
                  }}
                  className="w-full bg-white/5 hover:bg-white/10 text-slate-300 rounded-xl px-3 py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                >
                  <Navigation className="w-3 h-3" /> Centre map on aircraft
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Attribution note ── */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[9000] text-[10px] text-slate-700 pointer-events-none">
          Live data via <a href="https://adsb.lol" target="_blank" rel="noopener noreferrer" className="hover:text-slate-500 pointer-events-auto">adsb.lol</a> · ODbL licence
        </div>
      </div>
    </div>
  );
}
